import apiClient from "./middleware";

export type MvBrandArtistStatus = "pending" | "approved" | "approved_no_artist";

export interface BrandArtistLookupEntry {
  id: string;
  brandKey: string;
  artist: string | null;
  status: MvBrandArtistStatus;
  reviewedBy?: string;
  reviewedAt?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type MvMetadataGapReviewStatus =
  | "pending"
  | "selected"
  | "applied"
  | "skipped";

export interface MvMetadataGapReviewEntry {
  id: string;
  musicVideoAssetId: string;
  customId: string;
  episodeBrand?: string;
  currentLabel?: string;
  currentArtist?: string;
  assetTitle?: string;
  dropdownOptions: string[];
  selectedArtist?: string;
  reviewStatus: MvMetadataGapReviewStatus;
  checked?: boolean;
  notes?: string;
  appliedAt?: string;
  appliedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface MvMetadataGapApplyLogEntry {
  id: string;
  musicVideoAssetId: string;
  fieldsUpdated: string[];
  status: "success" | "skipped" | "error";
  error?: string;
  timestamp: string;
  triggeredBy: string;
  dryRun?: boolean;
}

export interface MvMetadataGapApplySummary {
  dryRun: boolean;
  assetIds: string[];
  processed: number;
  labelsApplied: number;
  artistsApplied: number;
  reviewsQueued: number;
  skipped: number;
  errors: number;
  results: Array<{
    musicVideoAssetId: string;
    customId?: string;
    episodeBrand?: string;
    labelAction?: string;
    artistAction?: string;
    error?: string;
  }>;
}

export const mvMetadataGapApi = {
  getBrandArtistLookup: async (params?: {
    status?: MvBrandArtistStatus;
  }): Promise<{ entries: BrandArtistLookupEntry[] }> => {
    const { data } = await apiClient.get("/MusicControlSuit/MvBrandArtistLookup", {
      params,
    });
    return data;
  },

  saveBrandArtistLookup: async (body: {
    brandKey: string;
    artist?: string | null;
    status?: MvBrandArtistStatus;
    notes?: string;
  }): Promise<BrandArtistLookupEntry> => {
    const { data } = await apiClient.post(
      "/MusicControlSuit/MvBrandArtistLookup",
      body
    );
    return data;
  },

  seedBrandArtistLookup: async (force = false): Promise<{
    seeded: number;
    skipped: number;
  }> => {
    const { data } = await apiClient.post(
      "/MusicControlSuit/MvBrandArtistLookup/seed",
      { force }
    );
    return data;
  },

  importBrandArtistLookup: async (
    rows: Array<{
      brandKey: string;
      artist?: string | null;
      status?: MvBrandArtistStatus;
      notes?: string;
    }>
  ): Promise<{ imported: number }> => {
    const { data } = await apiClient.post(
      "/MusicControlSuit/MvBrandArtistLookup/import",
      { rows }
    );
    return data;
  },

  getReviews: async (params?: {
    reviewStatus?: MvMetadataGapReviewStatus;
    unmappedOnly?: boolean;
    readyToApplyOnly?: boolean;
    completedOnly?: boolean;
    brand?: string;
  }): Promise<{ entries: MvMetadataGapReviewEntry[] }> => {
    const { data } = await apiClient.get(
      "/MusicControlSuit/MvMetadataGapReviews",
      { params }
    );
    return data;
  },

  updateReview: async (
    assetId: string,
    body: {
      selectedArtist?: string;
      reviewStatus?: MvMetadataGapReviewStatus;
      checked?: boolean;
      notes?: string;
    }
  ): Promise<MvMetadataGapReviewEntry> => {
    const { data } = await apiClient.patch(
      `/MusicControlSuit/MvMetadataGapReviews/${assetId}`,
      body
    );
    return data;
  },

  syncFromBigQuery: async (): Promise<{ synced: number; queued: number }> => {
    const { data } = await apiClient.get("/MusicControlSuit/SyncMvMetadataGaps");
    return data;
  },

  applyReviews: async (
    items: Array<{
      musicVideoAssetId: string;
      artist?: string;
      episodeBrand?: string;
    }>
  ): Promise<MvMetadataGapApplySummary> => {
    const { data } = await apiClient.post(
      "/MusicControlSuit/MvMetadataGapReviews/apply",
      items
    );
    return data;
  },

  applyNowForBrand: async (
    brand: string
  ): Promise<MvMetadataGapApplySummary> => {
    const { data } = await apiClient.post(
      "/MusicControlSuit/MvMetadataGapReviews/apply-now",
      { brand }
    );
    return data;
  },

  getApplyLog: async (limit = 100): Promise<{
    entries: MvMetadataGapApplyLogEntry[];
  }> => {
    const { data } = await apiClient.get(
      "/MusicControlSuit/MvMetadataGapApplyLog",
      { params: { limit } }
    );
    return data;
  },

  getStats: async (): Promise<{
    pendingReviews: number;
    unmappedBrandReviews: number;
    pendingLookupBrands: number;
    approvedLookupBrands: number;
  }> => {
    const { data } = await apiClient.get("/MusicControlSuit/MvMetadataGapStats");
    return data;
  },
};
